import React, { useEffect, useMemo } from "react";
import { Box, Dialog, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { History } from "lucide-react";
import CustomTable from "../../common/CustomTable";
import { Column, useCommonReducer } from "../../../redux/slice/commonSlice";
import { getProductPricingHistory, useProductReducer } from "../../../redux/slice/product/createProductSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import { useModalStyles } from "../../common/useModalStyles";

interface ProductItemPriceHistoryProps {
    open: boolean;
    onClose: () => void;
}

const ProductItemPriceHistoryPopup: React.FC<ProductItemPriceHistoryProps> = ({
    open,
    onClose
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        cancelBtnSx,
    } = useModalStyles();
    const dispatch = useAppDispatch();

    const { productPricingHistory, productObject } = useProductReducer();
    const { macInfoList } = useCommonReducer();

    const columnsPriceItem: Column[] = useMemo(() => {
        return [
            { id: "effectiveDate", label: "Effective Date", minWidth: 100 },
            { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
            { id: "priceType", label: "File Type", minWidth: 100 },
            { id: "listPrice", label: "List Price", minWidth: 100, align: "right" },
            { id: "allowableUnitPrice", label: "Allowable", minWidth: 100, align: "right" },
            { id: "customPrice", label: "Custom Price", minWidth: 100, align: "right" },
            { id: "auditTimestamp", label: "Last Updated Time", minWidth: 100 },	//CS-1315
        ]
    }, [open]);

    useEffect(() => {
        if (open) {
            dispatch(getProductPricingHistory({productId: productObject.productId}))
        }
    }, [open]);

    const productPricingHistoryArry: any[] = useMemo(() => {
        let modifiedArray: any[] = [];
    //@ts-ignore
    productPricingHistory?.forEach(obj => {

        let modifiedObj = {
            ...obj,
            listPrice: <CustomCurrencyField externalValue={obj?.listPrice} displayType="text" />,
            customPrice: <CustomCurrencyField externalValue={obj?.customPrice} displayType="text" />,
            allowableUnitPrice: <CustomCurrencyField externalValue={obj?.allowableUnitPrice} displayType="text" />      
          }
      if (modifiedObj.priceType === "MAC") {
        let macName = macInfoList?.find(option => option.macId === obj.macId)?.macName;
        modifiedObj["priceType"] = "Local Fee Schedule" + (macName ? " - " + macName : "");
      } else if (modifiedObj.priceType === "NATIONAL") {
        modifiedObj["priceType"] = "National";
      }
      modifiedArray.push(modifiedObj);
    });
    return modifiedArray;
    }, [productPricingHistory]);

    return (
        <>
            <Dialog
                open={open}
                keepMounted
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                scroll="paper"
                PaperProps={{ sx: paperSx(isMobile) }}
                onClick={(event) => {
                    event.stopPropagation();
                }}
                aria-describedby="product-item-price-history-dialog-description"
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={headerSx}>
                        <Box sx={iconBoxSx(tokens.accent)}>
                            <History size={20} color={tokens.accent} strokeWidth={1.8} />
                        </Box>
                        <Box sx={{ flex: 1, pr: 4 }}>
                            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                                Pricing History
                            </Typography>
                            <Typography id="product-item-price-history-dialog-description" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                                View list, allowable, and custom pricing changes over time.
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                            <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            ...bodySx,
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                            scrollbarGutter: "stable",
                            "&::-webkit-scrollbar": { width: 6, height: 6 },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": {
                                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                                borderRadius: 4,
                            },
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <CustomTable
                                    columns={columnsPriceItem}
                                    rows={productPricingHistoryArry}
                                    disablePagination={true}
                                    borderBox={true}
                                    sx={{
                                        width: "100%",
                                        maxWidth: "100%",
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={footerSx}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1.5,
                                flexDirection: { xs: "column", sm: "row" },
                            }}
                        >
                            <Box
                                component="button"
                                type="button"
                                onClick={onClose}
                                sx={{
                                    ...cancelBtnSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.8,
                                    px: 3,
                                    py: 1.2,
                                    border: "1px solid",
                                    cursor: "pointer",
                                    order: { xs: 2, sm: 1 },
                                }}
                            >
                                Cancel
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default ProductItemPriceHistoryPopup;
